if (typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
  gsap.registerPlugin(ScrollTrigger);
}

class HbParallax extends HbElements.Component {
  static TYPES = ['background', 'layered', 'fixed', 'zoom', 'mouse'];

  onConnect() {
    if (HbElements.prefersReducedMotion()) return;

    const bg = this.querySelector('.group-block__parallax-bg');
    if (!bg || typeof gsap === 'undefined') return;

    this.type = (this.dataset.type || 'layered').toLowerCase();
    if (!HbParallax.TYPES.includes(this.type)) this.type = 'layered';
    // `fixed` and `zoom` have no meaningful axis, and the setting that feeds this is hidden for
    // them — but hiding only stops new edits, it does not clear a value stored under an earlier
    // type. Ignoring it here keeps a stale `horizontal` from stranding them on an unused axis.
    this.axis =
      (this.dataset.direction || 'vertical').toLowerCase() === 'horizontal' &&
      this.type !== 'fixed' &&
      this.type !== 'zoom'
        ? 'x'
        : 'y';

    // A pointer effect with no pointer would buy overflow it can never spend. The untouched
    // CSS `cover` state is the right static fallback, same as for reduced motion.
    if (this.type === 'mouse' && !window.matchMedia('(hover: hover)').matches) return;

    this._bg = bg;
    // Travel we want, as a fraction of section height. `zoom` asks for none, which collapses
    // the generic layout to a span box the size of the host.
    this._travelRatio = this.type === 'zoom' ? 0 : Math.max(this.numAttr('intensity', 40), 10) / 200;
    // Ceiling on upscaling past `cover`. Free overflow from `cover` is spent first;
    // this caps how much extra we buy when there is none. On a height-bound section
    // travel equals the zoom exactly, so this doubles as the effect-strength knob.
    // 1 = framing identical to parallax-off, but zero travel on height-bound sections.
    // The attribute is a raw percent and is divided here, not in Liquid, because
    // `{{ 115 | times: 0.01 }}` renders 1.1500000000000001.
    this._maxZoom = Math.max(this.numAttr('maxZoom', 115), 100) / 100;

    // Seeded so a zero-size element at connect (hidden tab, lazy section) can't bake
    // NaN into the tween; the ResizeObserver fills in real values once it has a box.
    this._fromY = 0;
    this._toY = 0;
    this._maxX = 0;
    this._contentMaxY = 0;

    // Only `layered` moves the content plane.
    this._content = this.type === 'layered' ? this.contentPlane() : [];
    this.refreshLayout();

    if (typeof ResizeObserver !== 'undefined') {
      this._observer = new ResizeObserver(() => this.onResize());
      this._observer.observe(this);
    }

    if (this.type === 'mouse') {
      this.bindPointer();
      return;
    }

    this._tween = gsap.timeline({
      scrollTrigger: {
        trigger: this,
        start: 'top bottom',
        end: 'bottom top',
        scrub: true,
        invalidateOnRefresh: true,
        onRefreshInit: () => this.refreshLayout(),
      },
    });

    if (this.type === 'zoom') {
      // _maxZoom is a per-connect constant, so this is the one tween with no function values.
      this._tween.fromTo(bg, { scale: 1 }, { scale: this._maxZoom, ease: 'none' }, 0);
    } else if (this.axis === 'x') {
      this._tween.fromTo(bg, { x: () => -this._maxX }, { x: () => this._maxX, ease: 'none' }, 0);
    } else {
      // Background lags the page: translates down as the page scrolls down. `fixed` rides the
      // same scrub with endpoints that cancel the host's own movement exactly.
      this._tween.fromTo(bg, { y: () => this._fromY }, { y: () => this._toY, ease: 'none' }, 0);
    }

    // Content leads it. The differential between the two planes is what reads as depth,
    // and it costs no extra background zoom.
    if (this._content.length) {
      this._tween.fromTo(
        this._content,
        { y: () => this._contentMaxY },
        { y: () => -this._contentMaxY, ease: 'none' },
        0
      );
    }
  }

  bindPointer() {
    const bg = this._bg;
    // quickTo reuses one tween and mutates its end value, so pointermove needs no throttle.
    this._quickX = gsap.quickTo(bg, 'x', { duration: 0.6, ease: 'power3.out' });
    this._quickY = gsap.quickTo(bg, 'y', { duration: 0.6, ease: 'power3.out' });

    // Pointer as -1..1 from the centre, spent against the same rectangular budget the layout
    // bought, so both axes at maximum is still inside the overflow. Moving away from the
    // pointer is what reads as depth.
    this._onPointerMove = (event) => {
      const rect = this.getBoundingClientRect();
      this._quickX((1 - ((event.clientX - rect.left) / rect.width) * 2) * this._maxX);
      this._quickY((1 - ((event.clientY - rect.top) / rect.height) * 2) * this._toY);
    };
    // Touch never reaches this path, so pointerleave alone settles it.
    this._onPointerLeave = () => {
      this._quickX(0);
      this._quickY(0);
    };

    this.addEventListener('pointermove', this._onPointerMove);
    this.addEventListener('pointerleave', this._onPointerLeave);
  }

  onResize() {
    const w = this.offsetWidth;
    const h = this.offsetHeight;
    if (!w || !h) return;
    if (w === this._w && h === this._h) return;
    this.refreshLayout();
    this.refreshTrigger();
  }

  refreshLayout() {
    const bg = this._bg;
    if (!bg) return;

    const w = this.offsetWidth;
    const h = this.offsetHeight;
    if (!w || !h) return;

    this._w = w;
    this._h = h;

    if (this.type === 'fixed') {
      // Across the scrub the host's top in viewport coordinates goes vh - p(vh + h), so
      // cancelling it is y = -vh + p(vh + h) — linear in p, hence exact on a plain fromTo.
      this._fromY = -window.innerHeight;
      this._toY = h;
      bg.style.backgroundSize = 'cover';
      bg.style.backgroundPosition = 'center';
      // Overshoot the viewport by 1px on each edge. Pinning puts the span's edges exactly on
      // the viewport's, which leaves no margin for the sub-pixel error between the reported
      // scroll offset and the interpolated y — same reason the travel clamp below drops 1px.
      bg.style.top = '-1px';
      bg.style.left = '0';
      bg.style.width = '100%';
      // 100vh resolves to lvh on iOS, which is >= innerHeight, so a collapsing URL bar only
      // ever adds coverage below the fold. Do not swap in `${window.innerHeight}px`.
      bg.style.height = 'calc(100vh + 2px)';
      return;
    }

    const src = this.backgroundUrl(bg);
    if (src !== this._src) {
      this._src = src;
      this._natural = null;
    }

    if (!this._natural) {
      // Intrinsic size unknown — render exactly like parallax-off until it lands.
      bg.style.backgroundSize = 'cover';
      bg.style.backgroundPosition = 'center';
      this.applyLayout(h, 0, 0);
      this.loadNatural(src);
      return;
    }

    const nw = this._natural.w;
    const nh = this._natural.h;
    const desiredX = this.axis === 'x' || this.type === 'mouse' ? w * this._travelRatio : 0;
    const desiredY = this.axis === 'y' || this.type === 'mouse' ? h * this._travelRatio : 0;
    // The scale CSS `cover` would pick — the parallax-off baseline.
    const cover = Math.max(w / nw, h / nh);
    // Horizontal headroom is not free — `cover` supplies none on a tall image — so both axes
    // buy theirs through the same scale.
    const need = Math.max((w + desiredX) / nw, (h + desiredY) / nh);
    // Never below cover (would expose edges), never more than _maxZoom above it.
    const scale = Math.max(cover, Math.min(need, cover * this._maxZoom));
    // Travel is limited by whatever overflow that scale actually yields on each axis,
    // less 1px so subpixel rounding can never expose an edge at the extremes. Clamping
    // per axis means simultaneous max-X and max-Y is still inside the overflow.
    const travelX = Math.max(0, Math.min(desiredX, nw * scale - w - 1));
    const travelY = Math.max(0, Math.min(desiredY, nh * scale - h - 1));

    bg.style.backgroundSize = `${nw * scale}px ${nh * scale}px`;
    bg.style.backgroundPosition = 'center';
    this.applyLayout(h, travelX, travelY);
  }

  applyLayout(h, travelX, travelY) {
    this._maxX = travelX / 2;
    this._toY = travelY / 2;
    this._fromY = -this._toY;
    this._bg.style.top = `${-this._toY}px`;
    this._bg.style.left = `${-this._maxX}px`;
    // Width stays percentage-based: `100%` on an absolutely positioned child resolves against
    // the padding box while offsetWidth is the border box, so px would drift under a border.
    this._bg.style.width = `calc(100% + ${travelX}px)`;
    this._bg.style.height = `${h + travelY}px`;

    // Counter-motion for the content plane, bounded by the real gap between the content
    // and the clip edge — padding plus any unfilled space — so it can never clip.
    this._contentMaxY = Math.min(travelY * 0.5, this.contentRoom(h)) / 2;
  }

  contentRoom(h) {
    return this.roomFor(this._content, h);
  }

  // Smallest distance from the content to the clip edge: padding plus unfilled space.
  roomFor(nodes, h) {
    let room = Infinity;
    for (const node of nodes) {
      if (!node.offsetHeight && !node.offsetWidth) continue;
      const top = this.offsetTopWithin(node);
      room = Math.min(room, top, h - (top + node.offsetHeight));
    }
    return room === Infinity ? 0 : Math.max(0, room);
  }

  // Layout offset relative to this element — unaffected by the transforms we apply.
  offsetTopWithin(node) {
    let y = 0;
    let n = node;
    while (n && n !== this) {
      y += n.offsetTop;
      n = n.offsetParent;
    }
    return y;
  }

  contentPlane() {
    let plane = [];
    for (const child of this.children) {
      if (child === this._bg || child.tagName === 'STYLE' || child.tagName === 'SCRIPT') continue;
      // display:contents wrappers (the CTA link) generate no box, so move their children.
      if (getComputedStyle(child).display === 'contents') {
        for (const inner of child.children) {
          if (inner.tagName !== 'STYLE' && inner.tagName !== 'SCRIPT') plane.push(inner);
        }
      } else {
        plane.push(child);
      }
    }

    // A wrapper that fills the block leaves no room to move — the slack is inside it,
    // in its own padding. Step down until there is something to work with.
    const h = this.offsetHeight;
    for (let depth = 0; h && depth < 3 && this.roomFor(plane, h) <= 0; depth++) {
      const next = [];
      let expanded = false;
      for (const node of plane) {
        const kids = [...node.children].filter(
          (k) => k.tagName !== 'STYLE' && k.tagName !== 'SCRIPT'
        );
        if (kids.length && node.offsetHeight >= h) {
          next.push(...kids);
          expanded = true;
        } else {
          next.push(node);
        }
      }
      if (!expanded) break;
      plane = next;
    }

    return plane;
  }

  loadNatural(src) {
    if (!src || this._loading === src) return;
    this._loading = src;

    const img = new Image();
    img.onload = () => {
      this._loading = null;
      if (!this._bg || !img.naturalWidth || !img.naturalHeight) return;
      this._natural = { w: img.naturalWidth, h: img.naturalHeight };
      this.refreshLayout();
      this.refreshTrigger();
    };
    img.src = src;
  }

  refreshTrigger() {
    if (this._tween && this._tween.scrollTrigger) this._tween.scrollTrigger.refresh();
  }

  backgroundUrl(el) {
    const raw = getComputedStyle(el).backgroundImage;
    if (!raw || raw === 'none') return '';
    const match = raw.match(/url\((['"]?)(.*?)\1\)/);
    return match ? match[2] : '';
  }

  onDisconnect() {
    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
    }
    if (this._tween) {
      if (this._tween.scrollTrigger) this._tween.scrollTrigger.kill();
      this._tween.kill();
      this._tween = null;
    }
    if (this._content) {
      if (this._content.length && typeof gsap !== 'undefined') {
        gsap.set(this._content, { clearProps: 'transform' });
      }
      this._content = null;
    }
    if (this._onPointerMove) {
      this.removeEventListener('pointermove', this._onPointerMove);
      this.removeEventListener('pointerleave', this._onPointerLeave);
      this._onPointerMove = null;
      this._onPointerLeave = null;
      // Each quickTo holds a persistent tween on the global timeline.
      this._quickX.tween.kill();
      this._quickY.tween.kill();
      this._quickX = null;
      this._quickY = null;
    }
    if (this._bg && typeof gsap !== 'undefined') {
      gsap.set(this._bg, { clearProps: 'transform' });
    }
    this._bg = null;
  }
}

class HbFlipCard extends HbElements.Component {
  onConnect() {
    this.faces = Array.from(this.children).filter((child) => {
      if (child.nodeType !== 1) return false;
      if (child.tagName === 'STYLE' || child.tagName === 'SCRIPT') return false;
      return child.classList.contains('shopify-theme-block');
    });

    if (this.faces.length < 2) return;

    this.enableInterval = this.boolAttr('enableInterval');
    this.enableScroll = this.boolAttr('enableScroll');
    this.loop = this.boolAttr('loop', true);
    this.pauseHover = this.boolAttr('pauseHover', true);
    this.duration = Math.max(this.numAttr('duration', 600), 200) / 1000;
    this.intervalMs = Math.max(this.numAttr('interval', 3000), 500);
    this.axis = this.dataset.axis === 'x' ? 'x' : 'y';
    this.prop = this.axis === 'x' ? 'rotateX' : 'rotateY';
    this.lastIndex = this.faces.length - 1;
    this.scrollRangeFactor = 1.3;
    this.index = 0;
    this.activeTl = null;
    this.timer = null;
    this.scrollTrigger = null;
    this.scrollOwns = false;
    this.intervalArmed = false;
    this.boundPauseEnter = this.onIntervalPauseEnter.bind(this);
    this.boundPauseLeave = this.onIntervalPauseLeave.bind(this);

    this.faces.forEach((face, i) => {
      face.setAttribute('data-flip-face', String(i));
    });
    this.settle(0);

    if (HbElements.prefersReducedMotion()) {
      this.classList.add('is-reduced');
      return;
    }

    if (this.enableScroll) this.setupScroll();
    if (this.enableInterval && this.pauseHover) {
      this.addEventListener('pointerenter', this.boundPauseEnter);
      this.addEventListener('pointerleave', this.boundPauseLeave);
      this.addEventListener('focusin', this.boundPauseEnter);
      this.addEventListener('focusout', this.boundPauseLeave);
    }
    if (this.enableInterval && !this.scrollOwns) this.startInterval();
  }

  onDisconnect() {
    this.stopInterval();
    if (this.scrollTrigger) {
      this.scrollTrigger.kill();
      this.scrollTrigger = null;
    }
    if (this.activeTl) {
      this.activeTl.kill();
      this.activeTl = null;
    }
    if (this.boundPauseEnter) {
      this.removeEventListener('pointerenter', this.boundPauseEnter);
      this.removeEventListener('pointerleave', this.boundPauseLeave);
      this.removeEventListener('focusin', this.boundPauseEnter);
      this.removeEventListener('focusout', this.boundPauseLeave);
    }
  }

  resolveIndex(raw, allowLoop) {
    if (allowLoop) {
      return ((raw % this.faces.length) + this.faces.length) % this.faces.length;
    }
    return Math.max(0, Math.min(this.lastIndex, raw));
  }

  settle(activeIndex) {
    this.index = activeIndex;

    this.faces.forEach((face, i) => {
      const active = i === activeIndex;
      if (typeof gsap !== 'undefined') gsap.killTweensOf(face);
      face.classList.toggle('is-active', active);
      face.setAttribute('aria-hidden', active ? 'false' : 'true');
      face.style.visibility = active ? 'visible' : 'hidden';
      face.style.pointerEvents = 'none';
      if (typeof gsap !== 'undefined') {
        gsap.set(face, {
          clearProps: 'transform',
          opacity: active ? 1 : 0,
          zIndex: active ? 2 : 0,
        });
      }
    });
  }

  stopInterval() {
    this.intervalArmed = false;
    if (this.timer) {
      window.clearTimeout(this.timer);
      this.timer = null;
    }
  }

  scheduleInterval() {
    if (!this.enableInterval || !this.intervalArmed || this.scrollOwns) return;
    if (this.timer) {
      window.clearTimeout(this.timer);
      this.timer = null;
    }
    this.timer = window.setTimeout(() => {
      this.timer = null;
      if (!this.intervalArmed || this.scrollOwns) return;
      this.goNext();
    }, this.intervalMs);
  }

  startInterval() {
    if (!this.enableInterval) return;
    this.intervalArmed = true;
    this.scheduleInterval();
  }

  flipTo(nextIndex, forceDirection, onDone, allowLoop) {
    const canLoop = typeof allowLoop === 'boolean' ? allowLoop : this.loop && !this.scrollOwns;
    nextIndex = this.resolveIndex(nextIndex, canLoop);

    if (this.activeTl) {
      this.activeTl.eventCallback('onComplete', null);
      this.activeTl.kill();
      this.activeTl = null;
      this.settle(this.index);
    }

    if (nextIndex === this.index) {
      if (typeof onDone === 'function') onDone();
      return;
    }

    const current = this.faces[this.index];
    const next = this.faces[nextIndex];
    let forward = nextIndex > this.index;
    if (canLoop && this.index === this.lastIndex && nextIndex === 0) forward = true;
    if (canLoop && this.index === 0 && nextIndex === this.lastIndex) forward = false;
    if (forceDirection === 1) forward = true;
    if (forceDirection === -1) forward = false;

    const outAngle = forward ? -90 : 90;
    const inAngle = forward ? 90 : -90;

    this.faces.forEach((face) => {
      if (face !== current && face !== next) {
        face.classList.remove('is-active');
        face.style.visibility = 'hidden';
        face.style.pointerEvents = 'none';
        if (typeof gsap !== 'undefined') {
          gsap.set(face, { clearProps: 'transform', opacity: 0, zIndex: 0 });
        }
      }
    });

    current.style.visibility = 'visible';
    next.style.visibility = 'visible';
    current.style.pointerEvents = 'none';
    next.style.pointerEvents = 'none';
    current.classList.add('is-active');
    next.classList.add('is-active');

    if (typeof gsap === 'undefined') {
      this.settle(nextIndex);
      if (typeof onDone === 'function') onDone();
      return;
    }

    gsap.set(current, { [this.prop]: 0, opacity: 1, zIndex: 2 });
    gsap.set(next, { [this.prop]: inAngle, opacity: 0, zIndex: 3 });

    this.activeTl = gsap.timeline({
      onComplete: () => {
        this.activeTl = null;
        this.settle(nextIndex);
        if (typeof onDone === 'function') onDone();
      },
    });

    this.activeTl
      .to(current, {
        [this.prop]: outAngle,
        opacity: 0,
        duration: this.duration / 2,
        ease: 'power2.in',
      })
      .add(() => {
        current.classList.remove('is-active');
        current.style.visibility = 'hidden';
      })
      .set(next, { opacity: 1 })
      .to(next, {
        [this.prop]: 0,
        duration: this.duration / 2,
        ease: 'power2.out',
      });
  }

  goNext() {
    if (this.scrollOwns) return;
    const nextIndex = this.index + 1;
    if (nextIndex > this.lastIndex && !this.loop) {
      this.scheduleInterval();
      return;
    }
    this.flipTo(
      nextIndex,
      1,
      () => {
        this.scheduleInterval();
      },
      this.loop
    );
  }

  onIntervalPauseEnter() {
    if (!this.enableInterval || !this.pauseHover) return;
    this.stopInterval();
  }

  onIntervalPauseLeave() {
    if (!this.enableInterval || !this.pauseHover) return;
    if (!this.scrollOwns) this.startInterval();
  }

  scrollFaceFromProgress(progress) {
    return Math.round(Math.min(1, Math.max(0, progress)) * this.lastIndex);
  }

  syncScrollFace(target, animate) {
    if (target === this.lastTarget && this.lastTarget >= 0) return;
    const prev = this.lastTarget;
    this.lastTarget = target;

    if (!animate || prev < 0 || Math.abs(target - prev) > 1) {
      if (this.activeTl) {
        this.activeTl.eventCallback('onComplete', null);
        this.activeTl.kill();
        this.activeTl = null;
      }
      this.settle(target);
      return;
    }

    this.flipTo(target, target > prev ? 1 : -1, null, false);
  }

  setupScroll() {
    if (typeof ScrollTrigger === 'undefined') return;

    this.lastTarget = -1;

    this.scrollTrigger = ScrollTrigger.create({
      trigger: this,
      start: 'top bottom',
      end: () => `+=${Math.max(this.offsetHeight * this.scrollRangeFactor, 100)}`,
      invalidateOnRefresh: true,
      onEnter: () => {
        this.scrollOwns = true;
        this.stopInterval();
      },
      onEnterBack: () => {
        this.scrollOwns = true;
        this.stopInterval();
      },
      onLeave: () => {
        this.scrollOwns = false;
        if (this.enableInterval) this.startInterval();
      },
      onLeaveBack: () => {
        this.scrollOwns = false;
        if (this.enableInterval) this.startInterval();
      },
      onUpdate: (self) => {
        this.syncScrollFace(this.scrollFaceFromProgress(self.progress), true);
      },
      onRefresh: (self) => {
        this.syncScrollFace(this.scrollFaceFromProgress(self.progress), false);
        if (self.isActive) {
          this.scrollOwns = true;
          this.stopInterval();
        }
      },
    });

    this.syncScrollFace(this.scrollFaceFromProgress(this.scrollTrigger.progress), false);
    if (this.scrollTrigger.isActive) {
      this.scrollOwns = true;
      this.stopInterval();
    }
  }
}

class HbMotion extends HbElements.Component {
  static DIRECTIONS = [
    'up',
    'down',
    'left',
    'right',
    'up-left',
    'up-right',
    'down-left',
    'down-right',
    'near',
    'far',
    'cw',
    'ccw',
  ];

  static PLANAR_DIRECTIONS = [
    'up',
    'down',
    'left',
    'right',
    'up-left',
    'up-right',
    'down-left',
    'down-right',
  ];

  onConnect() {
    this.motion = (this.dataset.motion || 'none').toLowerCase();
    const defaultDirection = this.motion === 'cutout' || this.motion === 'wipe' ? 'right' : 'up';
    this.direction = (this.dataset.direction || defaultDirection).toLowerCase();

    if (this.motion === 'fade-up') {
      this.motion = 'fade';
      this.direction = 'up';
    } else if (this.motion === 'flip-up') {
      this.motion = 'flip';
      this.direction = 'up';
    }

    if (!HbMotion.DIRECTIONS.includes(this.direction)) {
      this.direction = defaultDirection;
    }

    if (this.motion === 'none' || typeof gsap === 'undefined') return;

    this.trigger = (this.dataset.trigger || 'enter').toLowerCase();
    this.duration = Math.max(this.numAttr('duration', 0.6), 0.1);
    this.intensity = Math.max(this.numAttr('intensity', 50), 1);
    this.stagger = Math.max(this.numAttr('stagger', 0.1), 0);
    this.delay = Math.max(this.numAttr('delay', 0), 0);
    this.targetMode = (this.dataset.target || 'first').toLowerCase();
    this.order = (this.dataset.order || 'forward').toLowerCase();
    if (!['together', 'forward', 'reverse', 'center', 'edges'].includes(this.order)) {
      this.order = 'forward';
    }
    this.targets = this.resolveTargets();
    this._tweens = [];
    this._scrollTrigger = null;
    this._revealed = false;
    this._motionStarted = false;
    this._revealTween = null;
    this._setup = null;
    this._run = null;
    this._pendingReady = null;
    this._completeCall = null;
    this._completed = false;
    this._syncParent = null;
    this._onParentWillStart = null;
    this._onParentStart = null;
    this._onParentComplete = null;
    this._onParentTargetStart = null;
    this._onParentTargetComplete = null;
    this._onParentHide = null;
    this._completedTargets = new Set();

    this.sync = (this.dataset.sync || 'independent').toLowerCase();
    if (!['independent', 'before', 'while', 'after'].includes(this.sync)) {
      this.sync = 'independent';
    }

    if (this.sync !== 'independent') {
      const parent = this.parentElement ? this.parentElement.closest('hb-motion') : null;
      if (parent) {
        this._syncParent = parent;
      } else {
        this.sync = 'independent';
      }
    }

    if (!this.targets.length) {
      // Nested-only host with no animateable targets: stay visible, emit lifecycle.
      this.classList.add('is-ready');
      if (typeof gsap !== 'undefined') gsap.set(this, { autoAlpha: 1, transition: 'none' });
      if (HbElements.prefersReducedMotion()) return;
      this.buildHostClock();
      return;
    }

    if (HbElements.prefersReducedMotion()) {
      this.classList.add('is-reduced');
      this.classList.add('is-ready');
      gsap.set(this, { autoAlpha: 1 });
      this.applyStaticEndState();
      return;
    }

    // Synced children stay host-visible so parent can cutout/wipe/fade this node.
    // Own motion runs on inner targets after the parent's slot for this node.
    if (this._syncParent) {
      this.classList.add('is-ready');
      gsap.set(this, { autoAlpha: 1, transition: 'none' });
      this._revealed = true;
      this.buildMotion();
      return;
    }

    this.classList.remove('is-ready');
    gsap.set(this, { autoAlpha: 0, transition: 'none' });
    this.buildMotion();
  }

  onDisconnect() {
    this.unbindParentSync();
    this.killMotion();
  }

  emitMotion(phase) {
    if (phase === 'will-start') {
      this._completedTargets.clear();
    }
    this.dispatchEvent(
      new CustomEvent(`hb:motion:${phase}`, {
        bubbles: false,
        detail: {
          phase,
          motion: this.motion,
          sync: this.sync,
        },
      })
    );
  }

  emitTargetPhase(phase, target) {
    if (phase === 'complete' && target) {
      this._completedTargets.add(target);
    }
    this.dispatchEvent(
      new CustomEvent(`hb:motion:target-${phase}`, {
        bubbles: false,
        detail: {
          phase,
          target,
          motion: this.motion,
        },
      })
    );
  }

  isParentSlotTarget() {
    if (!this._syncParent || !this._syncParent.targets) return false;
    return this._syncParent.targets.includes(this);
  }

  resetSyncedInner() {
    this.killMotionTweens();
    this._motionStarted = false;
    this._completed = false;
    this.classList.add('is-ready');
    gsap.set(this, { autoAlpha: 1, transition: 'none' });
    this._revealed = true;
    if (this._setup) this._setup();
  }

  playFromSync() {
    this.emitMotion('will-start');
    this.schedulePlay();
  }

  buildHostClock() {
    this._setup = () => {};
    this._run = () => {};

    if (this._syncParent) {
      this.bindParentSync();
      return;
    }

    const fire = () => {
      this.emitMotion('will-start');
      this.beginMotionRun();
    };

    const hide = () => {
      this._motionStarted = false;
      this._completed = false;
      if (this._completeCall) {
        this._completeCall.kill();
        this._completeCall = null;
      }
      this.emitMotion('hide');
    };

    if (typeof ScrollTrigger === 'undefined') {
      fire();
      return;
    }

    if (this.trigger === 'loop') {
      this._scrollTrigger = ScrollTrigger.create({
        trigger: this,
        start: 'top bottom',
        end: 'bottom top',
        onEnter: fire,
        onEnterBack: fire,
        onLeave: hide,
        onLeaveBack: hide,
      });
      if (this._scrollTrigger.isActive) fire();
      return;
    }

    if (this.trigger === 'load') {
      this._scrollTrigger = ScrollTrigger.create({
        trigger: this,
        start: 'top 88%',
        once: true,
        onEnter: fire,
      });
      return;
    }

    this._scrollTrigger = ScrollTrigger.create({
      trigger: this,
      start: 'top 88%',
      end: 'bottom top',
      onEnter: fire,
      onEnterBack: fire,
      onLeave: hide,
      onLeaveBack: hide,
    });

    if (this._scrollTrigger.isActive) fire();
  }

  resolveTargets() {
    const isBlock = (el) => {
      if (!el || el.nodeType !== 1) return false;
      if (el.tagName === 'STYLE' || el.tagName === 'SCRIPT') return false;
      return el.classList.contains('shopify-theme-block');
    };

    const direct = Array.from(this.children).filter(isBlock);
    if (direct.length) return this.filterTargets(direct);

    const cta = Array.from(this.children).find(
      (el) => el.nodeType === 1 && el.classList.contains('group-block__cta-link')
    );
    if (!cta) return [];
    return this.filterTargets(Array.from(cta.children).filter(isBlock));
  }

  filterTargets(nodes) {
    if (this.targetMode === 'all') return nodes;
    return nodes.slice(0, 1);
  }

  staggerAmount() {
    if (this.targetMode !== 'all') return 0;
    if (this.order === 'together') return 0;
    return this.stagger;
  }

  staggerConfig() {
    const each = this.staggerAmount();
    if (each <= 0) return 0;

    const fromMap = {
      forward: 'start',
      reverse: 'end',
      center: 'center',
      edges: 'edges',
    };

    return {
      each,
      from: fromMap[this.order] || 'start',
    };
  }

  staggerDelay(index) {
    const each = this.staggerAmount();
    const total = this.targets.length;
    if (each <= 0 || total <= 1) return 0;

    const mid = (total - 1) / 2;
    let position = index;

    if (this.order === 'reverse') {
      position = total - 1 - index;
    } else if (this.order === 'center') {
      position = Math.abs(index - mid);
    } else if (this.order === 'edges') {
      position = mid - Math.abs(index - mid);
    }

    return position * each;
  }

  killMotionTweens() {
    if (this._completeCall) {
      this._completeCall.kill();
      this._completeCall = null;
    }
    if (this._revealTween) {
      this._revealTween.kill();
      this._revealTween = null;
    }
    this._pendingReady = null;
    if (this._tweens) {
      this._tweens.forEach((t) => {
        if (!t) return;
        if (t.scrollTrigger) t.scrollTrigger.kill();
        t.kill();
      });
      this._tweens = [];
    }
    this.targets?.forEach((el) => {
      if (typeof gsap !== 'undefined') gsap.killTweensOf(el);
    });
    this._motionStarted = false;
    this._revealed = false;
    this._completed = false;
    this._completedTargets?.clear();
  }

  killMotion() {
    if (this._scrollTrigger) {
      this._scrollTrigger.kill();
      this._scrollTrigger = null;
    }
    this.killMotionTweens();
    if (typeof gsap !== 'undefined') gsap.killTweensOf(this);
  }

  track(tween) {
    if (tween) this._tweens.push(tween);
    return tween;
  }

  applyStaticEndState() {
    this.targets.forEach((el) => {
      gsap.set(el, {
        clearProps: 'clipPath,transform,opacity,filter,x,y,xPercent,yPercent,scale,rotate,rotateX,rotateY',
        opacity: 1,
        scale: 1,
        x: 0,
        y: 0,
        xPercent: 0,
        yPercent: 0,
        rotate: 0,
        rotateX: 0,
        rotateY: 0,
        clipPath: 'none',
      });
    });
  }

  planarDirection() {
    if (HbMotion.PLANAR_DIRECTIONS.includes(this.direction)) return this.direction;
    return this.motion === 'cutout' || this.motion === 'wipe' ? 'right' : 'up';
  }

  directionalOffset(distance) {
    switch (this.planarDirection()) {
      case 'down':
        return { y: -distance };
      case 'left':
        return { x: distance };
      case 'right':
        return { x: -distance };
      case 'up-left':
        return { x: distance, y: distance };
      case 'up-right':
        return { x: -distance, y: distance };
      case 'down-left':
        return { x: distance, y: -distance };
      case 'down-right':
        return { x: -distance, y: -distance };
      case 'up':
      default:
        return { y: distance };
    }
  }

  directionalInset(amount) {
    switch (this.planarDirection()) {
      case 'up':
        return `inset(${amount}% 0 0 0)`;
      case 'down':
        return `inset(0 0 ${amount}% 0)`;
      case 'left':
        return `inset(0 0 0 ${amount}%)`;
      case 'up-left':
        return `inset(${amount}% 0 0 ${amount}%)`;
      case 'up-right':
        return `inset(${amount}% ${amount}% 0 0)`;
      case 'down-left':
        return `inset(0 0 ${amount}% ${amount}%)`;
      case 'down-right':
        return `inset(0 ${amount}% ${amount}% 0)`;
      case 'right':
      default:
        return `inset(0 ${amount}% 0 0)`;
    }
  }

  spinSign() {
    if (this.direction === 'ccw') return -1;
    return 1;
  }

  pauseTracked() {
    this._tweens.forEach((t) => {
      if (t && typeof t.pause === 'function') t.pause();
    });
  }

  playTracked() {
    this._tweens.forEach((t) => {
      if (t && typeof t.play === 'function') t.play();
    });
  }

  hideHost() {
    if (this._revealTween) {
      this._revealTween.kill();
      this._revealTween = null;
    }
    this._pendingReady = null;
    this.classList.remove('is-ready');
    gsap.set(this, { autoAlpha: 0, transition: 'none' });
    this._revealed = false;
    this.emitMotion('hide');
  }

  resetHidden() {
    this.killMotionTweens();
    this.classList.remove('is-ready');
    gsap.set(this, { autoAlpha: 0, transition: 'none' });
    this._revealed = false;
    this._motionStarted = false;
    if (this._setup) this._setup();
  }

  armComplete() {
    if (this._completeCall) {
      this._completeCall.kill();
      this._completeCall = null;
    }
    const n = Math.max(1, this.targets.length);
    const staggerExtra = this.staggerAmount() * Math.max(0, n - 1);
    const at = Math.max(0.01, this.duration + staggerExtra);
    this._completeCall = gsap.delayedCall(at, () => {
      this._completeCall = null;
      this._completed = true;
      this.emitMotion('complete');
    });
    this.track(this._completeCall);
  }

  beginMotionRun() {
    this._completed = false;
    this._motionStarted = true;
    this.emitMotion('start');
    if (this._run) this._run();
    this.armComplete();
  }

  schedulePlay() {
    this.revealThen(() => this.beginMotionRun());
  }

  bindParentSync() {
    if (!this._syncParent) return;

    this._onParentWillStart = () => {
      this.resetSyncedInner();
      if (this.sync !== 'before') return;
      if (this.isParentSlotTarget()) return;
      this.playFromSync();
    };

    this._onParentStart = () => {
      if (this.sync !== 'while') return;
      if (this.isParentSlotTarget()) return;
      this.playFromSync();
    };

    this._onParentComplete = () => {
      if (this.sync !== 'after') return;
      if (this.isParentSlotTarget()) return;
      this.playFromSync();
    };

    this._onParentTargetStart = (event) => {
      if (event.detail?.target !== this) return;
      if (this.sync === 'before' || this.sync === 'while') this.playFromSync();
    };

    this._onParentTargetComplete = (event) => {
      if (event.detail?.target !== this) return;
      if (this.sync === 'after') this.playFromSync();
    };

    this._onParentHide = () => {
      this.killMotionTweens();
      if (this._setup) this._setup();
      if (this.isParentSlotTarget()) {
        this.classList.add('is-ready');
        gsap.set(this, { autoAlpha: 1, transition: 'none' });
        this._revealed = true;
        return;
      }
      this.hideHost();
    };

    this._syncParent.addEventListener('hb:motion:will-start', this._onParentWillStart);
    this._syncParent.addEventListener('hb:motion:start', this._onParentStart);
    this._syncParent.addEventListener('hb:motion:complete', this._onParentComplete);
    this._syncParent.addEventListener('hb:motion:target-start', this._onParentTargetStart);
    this._syncParent.addEventListener('hb:motion:target-complete', this._onParentTargetComplete);
    this._syncParent.addEventListener('hb:motion:hide', this._onParentHide);

    // Late connect catch-up.
    if (this._syncParent._motionStarted) {
      if (this.isParentSlotTarget()) {
        if (this.sync === 'after' && this._syncParent._completedTargets?.has(this)) {
          this.playFromSync();
        }
      } else if (this.sync === 'before' || this.sync === 'while') {
        this.playFromSync();
      } else if (this.sync === 'after' && this._syncParent._completed) {
        this.playFromSync();
      }
    }
  }

  unbindParentSync() {
    if (!this._syncParent) return;
    if (this._onParentWillStart) {
      this._syncParent.removeEventListener('hb:motion:will-start', this._onParentWillStart);
    }
    if (this._onParentStart) {
      this._syncParent.removeEventListener('hb:motion:start', this._onParentStart);
    }
    if (this._onParentComplete) {
      this._syncParent.removeEventListener('hb:motion:complete', this._onParentComplete);
    }
    if (this._onParentTargetStart) {
      this._syncParent.removeEventListener('hb:motion:target-start', this._onParentTargetStart);
    }
    if (this._onParentTargetComplete) {
      this._syncParent.removeEventListener('hb:motion:target-complete', this._onParentTargetComplete);
    }
    if (this._onParentHide) {
      this._syncParent.removeEventListener('hb:motion:hide', this._onParentHide);
    }
    this._onParentWillStart = null;
    this._onParentStart = null;
    this._onParentComplete = null;
    this._onParentTargetStart = null;
    this._onParentTargetComplete = null;
    this._onParentHide = null;
    this._syncParent = null;
  }

  flushReady() {
    const onReady = this._pendingReady;
    this._pendingReady = null;
    if (!onReady) return;

    const go = () => onReady();
    if (this.delay > 0) {
      this.track(gsap.delayedCall(this.delay, go));
      return;
    }
    go();
  }

  revealThen(onReady) {
    this._pendingReady = onReady;
    this.classList.add('is-ready');

    if (this._revealed) {
      gsap.set(this, { autoAlpha: 1, transition: 'none' });
      this.flushReady();
      return;
    }

    // In-flight reveal: keep tween, only swap the ready callback.
    if (this._revealTween) return;

    gsap.set(this, { transition: 'none' });
    this._revealTween = gsap.to(this, {
      autoAlpha: 1,
      duration: 0.25,
      ease: 'power1.out',
      overwrite: 'auto',
      onComplete: () => {
        this._revealed = true;
        this._revealTween = null;
        this.flushReady();
      },
    });
  }

  startOrResumeLoop() {
    if (this._revealed && this._motionStarted) {
      this.classList.add('is-ready');
      gsap.set(this, { autoAlpha: 1 });
      this.playTracked();
      return;
    }

    this.emitMotion('will-start');
    this.revealThen(() => {
      if (!this._motionStarted) {
        this.beginMotionRun();
        return;
      }
      this.playTracked();
    });
  }

  replayEnter() {
    this.killMotionTweens();
    this._pendingReady = null;
    this.classList.remove('is-ready');
    gsap.set(this, { autoAlpha: 0, transition: 'none' });
    this._revealed = false;
    if (this._setup) this._setup();
    this.emitMotion('will-start');
    this.schedulePlay();
  }

  leaveEnter() {
    this.killMotionTweens();
    this._pendingReady = null;
    if (this._setup) this._setup();
    this.hideHost();
  }

  leaveLoop() {
    if (!this._motionStarted) {
      this.killMotionTweens();
    } else {
      this.pauseTracked();
    }
    this.hideHost();
  }

  buildMotion() {
    switch (this.motion) {
      case 'cutout':
        this.buildCutout();
        break;
      case 'tilt':
        this.buildTilt();
        break;
      case 'flicker':
        this.buildFlicker();
        break;
      case 'stamp':
        this.buildStamp();
        break;
      case 'float':
        this.buildFloat();
        break;
      case 'fade':
        this.buildFade();
        break;
      case 'wipe':
        this.buildWipe();
        break;
      case 'flip':
        this.buildFlip();
        break;
      default:
        break;
    }
  }

  withEnter(setup, run) {
    this._setup = setup;
    this._run = run;
    setup();

    if (this._syncParent) {
      this.bindParentSync();
      return;
    }

    if (typeof ScrollTrigger === 'undefined') {
      this.emitMotion('will-start');
      this.schedulePlay();
      return;
    }

    // Use this element (not parent) so visibility matches the motion box.
    if (this.trigger === 'loop') {
      this._scrollTrigger = ScrollTrigger.create({
        trigger: this,
        start: 'top bottom',
        end: 'bottom top',
        onEnter: () => this.startOrResumeLoop(),
        onEnterBack: () => this.startOrResumeLoop(),
        onLeave: () => this.leaveLoop(),
        onLeaveBack: () => this.leaveLoop(),
      });

      if (this._scrollTrigger.isActive) this.startOrResumeLoop();
      return;
    }

    if (this.trigger === 'load') {
      this._scrollTrigger = ScrollTrigger.create({
        trigger: this,
        start: 'top 88%',
        once: true,
        onEnter: () => {
          if (this._setup) this._setup();
          this.emitMotion('will-start');
          this.schedulePlay();
        },
      });
      return;
    }

    // enter: replay every viewport enter; hide on leave.
    // Use enter/leave callbacks only — onToggle+onUpdate double-fired and
    // killed in-flight reveals (opacity stuck mid-fade).
    this._scrollTrigger = ScrollTrigger.create({
      trigger: this,
      start: 'top 88%',
      end: 'bottom top',
      onEnter: () => this.replayEnter(),
      onEnterBack: () => this.replayEnter(),
      onLeave: () => this.leaveEnter(),
      onLeaveBack: () => this.leaveEnter(),
    });

    if (this._scrollTrigger.isActive) this.replayEnter();
  }

  buildCutout() {
    const steps = Math.max(3, Math.round(this.intensity / 20));
    const setup = () => {
      this.targets.forEach((el) => {
        gsap.set(el, { clipPath: this.directionalInset(100) });
      });
    };
    this.withEnter(setup, () => {
      this.targets.forEach((el, i) => {
        const tl = gsap.timeline({
          delay: this.staggerDelay(i),
          onStart: () => this.emitTargetPhase('start', el),
          onComplete: () => this.emitTargetPhase('complete', el),
        });
        const stepDur = this.duration / steps;
        for (let s = 1; s <= steps; s += 1) {
          const inset = Math.max(0, 100 - (s / steps) * 100);
          tl.to(el, {
            clipPath: this.directionalInset(inset),
            duration: stepDur,
            ease: 'steps(1)',
          });
        }
        this.track(tl);
      });
    });
  }

  buildTilt() {
    const deg = Math.min(18, this.intensity / 5);
    const sign = this.spinSign();
    const setup = () => {
      this.targets.forEach((el) => {
        gsap.set(el, { transformOrigin: '50% 50%', rotate: -deg * sign });
      });
    };
    this.withEnter(setup, () => {
      this.targets.forEach((el, i) => {
        this.track(
          gsap.to(el, {
            rotate: deg * sign,
            duration: Math.max(this.duration, 1.2),
            ease: 'sine.inOut',
            yoyo: true,
            repeat: -1,
            delay: this.staggerDelay(i),
            onStart: () => this.emitTargetPhase('start', el),
          })
        );
        this.track(
          gsap.delayedCall(this.staggerDelay(i) + this.duration, () => {
            this.emitTargetPhase('complete', el);
          })
        );
      });
    });
  }

  buildFlicker() {
    const depth = Math.min(0.85, this.intensity / 100);
    const setup = () => {
      this.targets.forEach((el) => {
        gsap.set(el, { opacity: 1 });
      });
    };
    this.withEnter(setup, () => {
      this.targets.forEach((el, i) => {
        const tl = gsap.timeline({
          repeat: -1,
          delay: this.staggerDelay(i),
          onStart: () => this.emitTargetPhase('start', el),
        });
        tl.to(el, { opacity: 1 - depth, duration: 0.05, ease: 'none' })
          .to(el, { opacity: 1, duration: 0.08, ease: 'none' })
          .to(el, { opacity: 1 - depth * 0.6, duration: 0.04, ease: 'none' })
          .to(el, { opacity: 1, duration: 0.12, ease: 'none' })
          .to(el, { opacity: 1, duration: Math.max(this.duration, 0.4), ease: 'none' })
          .to(el, { opacity: 1 - depth * 0.4, duration: 0.06, ease: 'none' })
          .to(el, { opacity: 1, duration: 0.1, ease: 'none' })
          .to(el, { opacity: 1, duration: Math.max(this.duration * 1.4, 0.6), ease: 'none' });
        this.track(tl);
        this.track(
          gsap.delayedCall(this.staggerDelay(i) + this.duration, () => {
            this.emitTargetPhase('complete', el);
          })
        );
      });
    });
  }

  buildStamp() {
    const fromScale = this.direction === 'far' ? 1.2 : 0.85;
    const setup = () => {
      this.targets.forEach((el) => {
        gsap.set(el, { scale: fromScale, opacity: 0, transformOrigin: '50% 50%' });
      });
    };
    this.withEnter(setup, () => {
      this.targets.forEach((el, i) => {
        this.track(
          gsap.to(el, {
            scale: 1,
            opacity: 1,
            duration: this.duration,
            ease: 'back.out(2)',
            delay: this.staggerDelay(i),
            onStart: () => this.emitTargetPhase('start', el),
            onComplete: () => this.emitTargetPhase('complete', el),
          })
        );
      });
    });
  }

  buildFloat() {
    const amp = Math.max(4, this.intensity / 8);
    const setup = () => {
      this.targets.forEach((el) => {
        gsap.set(el, { y: 0 });
      });
    };
    this.withEnter(setup, () => {
      this.targets.forEach((el, i) => {
        this.track(
          gsap.to(el, {
            y: amp,
            duration: Math.max(this.duration, 1.2),
            ease: 'sine.inOut',
            yoyo: true,
            repeat: -1,
            delay: this.staggerDelay(i),
            onStart: () => this.emitTargetPhase('start', el),
          })
        );
        this.track(
          gsap.delayedCall(this.staggerDelay(i) + this.duration, () => {
            this.emitTargetPhase('complete', el);
          })
        );
      });
    });
  }

  buildFade() {
    const dist = Math.max(12, this.intensity / 2);
    const spin = Math.min(45, this.intensity * 0.6);
    const setup = () => {
      this.targets.forEach((el) => {
        const from = { opacity: 0, x: 0, y: 0, scale: 1, rotate: 0 };

        if (this.direction === 'near') {
          from.scale = 0.85;
        } else if (this.direction === 'far') {
          from.scale = 1.15;
        } else if (this.direction === 'cw' || this.direction === 'ccw') {
          from.rotate = -spin * this.spinSign();
        } else {
          Object.assign(from, this.directionalOffset(dist));
        }

        gsap.set(el, from);
      });
    };
    this.withEnter(setup, () => {
      this.targets.forEach((el, i) => {
        this.track(
          gsap.to(el, {
            x: 0,
            y: 0,
            scale: 1,
            rotate: 0,
            opacity: 1,
            duration: this.duration,
            ease: 'power2.out',
            delay: this.staggerDelay(i),
            onStart: () => this.emitTargetPhase('start', el),
            onComplete: () => this.emitTargetPhase('complete', el),
          })
        );
      });
    });
  }

  buildWipe() {
    this.style.overflow = 'hidden';
    const offset = this.directionalOffset(101);
    const percentOffset = {
      xPercent: offset.x || 0,
      yPercent: offset.y || 0,
    };
    const setup = () => {
      this.targets.forEach((el) => {
        gsap.set(el, percentOffset);
      });
    };
    this.withEnter(setup, () => {
      this.targets.forEach((el, i) => {
        this.track(
          gsap.to(el, {
            xPercent: 0,
            yPercent: 0,
            duration: this.duration,
            ease: 'power2.out',
            delay: this.staggerDelay(i),
            onStart: () => this.emitTargetPhase('start', el),
            onComplete: () => this.emitTargetPhase('complete', el),
          })
        );
      });
    });
  }

  buildFlip() {
    const angle = Math.min(95, 45 + this.intensity * 0.5);
    const spin = Math.min(90, 40 + this.intensity * 0.5);
    const dir = this.direction;
    const from = {
      transformOrigin: '50% 50%',
      rotateX: 0,
      rotateY: 0,
      rotate: 0,
      scale: 1,
      opacity: 0,
      force3D: true,
      transformStyle: 'preserve-3d',
    };

    if (dir === 'near') {
      from.scale = 0.7;
      from.rotateX = angle * 0.35;
    } else if (dir === 'far') {
      from.scale = 1.25;
      from.rotateX = -angle * 0.35;
    } else if (dir === 'cw' || dir === 'ccw') {
      from.rotate = -spin * this.spinSign();
    } else if (dir === 'down') {
      from.transformOrigin = '50% 0%';
      from.rotateX = -angle;
    } else if (dir === 'left') {
      from.transformOrigin = '100% 50%';
      from.rotateY = angle;
    } else if (dir === 'right') {
      from.transformOrigin = '0% 50%';
      from.rotateY = -angle;
    } else if (dir === 'up-left') {
      from.transformOrigin = '100% 100%';
      from.rotateX = angle * 0.7;
      from.rotateY = angle * 0.7;
    } else if (dir === 'up-right') {
      from.transformOrigin = '0% 100%';
      from.rotateX = angle * 0.7;
      from.rotateY = -angle * 0.7;
    } else if (dir === 'down-left') {
      from.transformOrigin = '100% 0%';
      from.rotateX = -angle * 0.7;
      from.rotateY = angle * 0.7;
    } else if (dir === 'down-right') {
      from.transformOrigin = '0% 0%';
      from.rotateX = -angle * 0.7;
      from.rotateY = -angle * 0.7;
    } else {
      from.transformOrigin = '50% 100%';
      from.rotateX = angle;
    }

    gsap.set(this, { perspective: 900, transformStyle: 'preserve-3d' });
    const setup = () => {
      this.targets.forEach((el) => {
        gsap.set(el, from);
      });
    };
    this.withEnter(setup, () => {
      this.targets.forEach((el, i) => {
        this.track(
          gsap.to(el, {
            rotateX: 0,
            rotateY: 0,
            rotate: 0,
            scale: 1,
            opacity: 1,
            duration: this.duration,
            ease: 'power3.out',
            delay: this.staggerDelay(i),
            onStart: () => this.emitTargetPhase('start', el),
            onComplete: () => this.emitTargetPhase('complete', el),
          })
        );
      });
    });
  }
}

HbElements.define('hb-parallax', HbParallax);
HbElements.define('hb-flip-card', HbFlipCard);
HbElements.define('hb-motion', HbMotion);
